// new code 

import { useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Stack,
  TextField,
  MenuItem,
  IconButton,
  InputAdornment,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  FormControl,
  Select,
  FormHelperText,
  Divider,
} from '@mui/material';
import { Add, Remove, CalendarToday as CalendarTodayIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  setFormData,
  setErrors,
  setSameAsAddress,
  setCustomers,
  setBranches,
  setSelectedCustomerId,
  setSelectedCustomer,
  setMode,
  setSubmitting,
  setTotals,
  setInvoiceStatus,
  setOpenModal,
  setOpenAdvance,
  setOpenOrder,
  setOpenDelivery,
  setLoading,
} from '../../store/orderSlice';
import { setCartItems, updateQuantity } from '../../store/cartSlice';
import { useSnackbar } from '../../App';
import { createOrder, checkInvoiceNumber } from '../../service/orderService';
import { getAllBranchLists } from '../../service/branchService';
import { createCustomer, getAllCustomers } from '../../service/customerService';
import AddCustomer from '../Customer/AddCustomer';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Color from '../../components/Color';
import debounce from 'lodash.debounce';

const ORDER_TYPE = [
  { label: 'Quotation', value: 'quotation' },
  { label: 'Invoice', value: 'invoice' },
];

const DELIVERY_TYPE = [
  { label: 'Home Delivery', value: 'home_delivery' },
  { label: 'PickUp', value: 'pickup' },
];

const requiredFields = [
  'name',
  'phone',
  'address',
  'order_date',
  'delivery_date',
  'payment_method',
  'order_type',
  'branch_id',
  'packing_charges',
  'salesman_name',
  'delivery_type',
  'payment_date',
  'status',
];

const paymentMethods = [
  { label: 'Cash', value: 'cash' },
  { label: 'UPI', value: 'upi' },
  { label: 'Card', value: 'card' },
  { label: 'Bank Transfer', value: 'bank_transfer' },
];

const OrderDetails = () => {
  const { showSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const {
    formData,
    errors,
    sameAsAddress,
    customers,
    branches,
    selectedCustomerId,
    selectedCustomer,
    mode,
    submitting,
    totals,
    invoiceStatus,
    openModal,
    openAdvance,
    openOrder,
    openDelivery,
    loading,
  } = useSelector((state) => state.order);
  const { cartItems } = useSelector((state) => state.cart);
  const iconRef = useRef(null);
  const navigate = useNavigate();

  const settings = JSON.parse(localStorage.getItem('settings') || '{}');
  const invoicePrimaryColor = settings.invoice_primary_color || '#e60073';
  const invoiceSecondaryColor = settings.invoice_secondary_color || '#f2c0d7';
  const roleId = localStorage.getItem('role') || '2';
  const branchId = localStorage.getItem('branch') || '';
  const branchName = branches.find((b) => b.id === Number(branchId))?.name || 'Your Branch';

  const fetchCustomers = async () => {
    dispatch(setLoading(true));
    try {
      const res = await getAllCustomers();
      const data = res?.data;
      if (Array.isArray(data?.data)) {
        dispatch(setCustomers(data.data));
      } else {
        dispatch(setCustomers([]));
        showSnackbar('Unexpected response format!', 'error');
      }
    } catch (error) {
      showSnackbar('Failed to fetch customers!', 'error');
      dispatch(setCustomers([]));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const fetchBranches = async () => {
    dispatch(setLoading(true));
    try {
      const res = await getAllBranchLists();
      const list = res?.data?.data || [];
      dispatch(setBranches(Array.isArray(list) ? list : []));
    } catch (error) {
      showSnackbar('Failed to fetch branches!', 'error');
      dispatch(setBranches([]));
    } finally {
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, [dispatch]);

  useEffect(() => {
    fetchBranches();
  }, [dispatch]);

  useEffect(() => {
    if (roleId === '2' && branchId && branches.length > 0) {
      const selectedBranch = branches.find((b) => b.id === Number(branchId));
      if (selectedBranch) {
        dispatch(setFormData({
          branch_id: branchId,
          branch_code: selectedBranch.code || '',
        }));
      }
    }
  }, [dispatch, roleId, branchId, branches]);

  const checkInvoiceNumberDebounced = debounce(async (invoiceNumber) => {
    if (!invoiceNumber.trim()) {
      dispatch(setErrors({ ...errors, invoice_number: 'Invoice Number is required' }));
      dispatch(setInvoiceStatus({ exists: null, message: '' }));
      return;
    }

    try {
      const response = await checkInvoiceNumber({ invoice_number: invoiceNumber });
      const { exists, message } = response.data;
      dispatch(setInvoiceStatus({ exists, message }));
      dispatch(setErrors({ ...errors, invoice_number: exists ? message : '' }));
    } catch (error) {
      console.error('Error checking invoice number:', error);
      dispatch(setInvoiceStatus({ exists: null, message: '' }));
      dispatch(setErrors({ ...errors, invoice_number: 'Error checking invoice number' }));
    }
  }, 500);

  const handleBranchSelect = (e) => {
    const { name, value } = e.target;
    dispatch(setFormData({ [name]: value }));
    const selected = branches.find((b) => b.id === Number(value));
    if (selected) {
      dispatch(setFormData({ branch_code: selected.code }));
    }
    if (!value) {
      dispatch(setErrors({ ...errors, branch_id: 'Branch is required' }));
    } else {
      dispatch(setErrors({ ...errors, branch_id: '' }));
    }
  };

  const handleCustomerSelect = (e) => {
    const value = e.target.value;

    if (value === "new") {
      dispatch(setMode("new"));
      dispatch(setOpenModal(true));
      dispatch(setSelectedCustomer(null));
      dispatch(setFormData({
        ...formData,
        name: "",
        phone: "",
        address: "",
        contact_type: "",
        customer_id: "",
        delivery_address: sameAsAddress ? "" : formData.delivery_address,
      }));
      dispatch(setSelectedCustomerId(""));
      dispatch(setErrors({ ...errors, customer_id: "Customer selection is required" }));
    } else {
      const selected = customers.find((c) => c.id === parseInt(value));
      dispatch(setSelectedCustomerId(value));
      dispatch(setSelectedCustomer(selected));
      dispatch(setMode("existing"));

      if (selected) {
        dispatch(setFormData({
          ...formData,
          customer_id: selected.id.toString(),
          name: selected.name || "",
          phone: selected.phone || "",
          address: selected.address || "",
          contact_type: selected.contact_type || "",
          delivery_address: sameAsAddress ? selected.address || "" : formData.delivery_address || "",
        }));
        dispatch(setErrors({ ...errors, customer_id: "" }));
      }
    }
  };

  useEffect(() => {
    const storedProducts = localStorage.getItem('selectedProducts');
    dispatch(setCartItems(storedProducts ? JSON.parse(storedProducts) : []));
  }, [dispatch]);

  useEffect(() => {
    if (!cartItems || cartItems.length === 0) {
      dispatch(setTotals({ subtotal: 0, taxAmount: 0, total: 0 }));
      dispatch(setFormData({ pending: '0.00' }));
      return;
    }

    const newTotals = cartItems.reduce(
      (acc, item) => {
        const price = parseFloat(item.base_price || 0);
        const gst = parseFloat(item.tax_rate || 0);
        const unitPriceWithTax = price + (price * gst) / 100;
        const taxAmount = (price * gst) / 100;
        acc.subtotal += price * (item.quantity || 0);
        acc.taxAmount += taxAmount * (item.quantity || 0);
        acc.total += unitPriceWithTax * (item.quantity || 0);
        return acc;
      },
      { subtotal: 0, taxAmount: 0, total: 0 }
    );

    const packingCharges = parseFloat(formData.packing_charges) || 0;
    newTotals.total = newTotals.subtotal + newTotals.taxAmount + packingCharges;

    const adv = parseFloat(formData.advance_amount) || 0;
    const pending = newTotals.total - adv;

    dispatch(setTotals(newTotals));
    if (!isNaN(pending)) {
      dispatch(setFormData({ pending: pending.toFixed(2) }));
    }
  }, [formData.packing_charges, formData.advance_amount, cartItems, dispatch]);

  useEffect(() => {
    if (sameAsAddress) {
      dispatch(setFormData({ delivery_address: formData.address || '' }));
    }
  }, [sameAsAddress, formData.address, dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'invoice_number') {
      dispatch(setFormData({ [name]: value }));
      checkInvoiceNumberDebounced(value);
      return;
    }

    if (name === 'advance_amount' || name === 'packing_charges' || name === 'discount_percentage') {
      const numericValue = value === '' ? '' : Number(value);

      if (numericValue < 0) {
        dispatch(setErrors({
          ...errors,
          [name]: `${name.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())} cannot be negative`,
        }));
      } else if (name === 'advance_amount' && totals.total && numericValue > totals.total) {
        dispatch(setErrors({ ...errors, advance_amount: 'Advance cannot be more than total amount' }));
      } else if (name === 'discount_percentage' && numericValue > 100) {
        dispatch(setErrors({ ...errors, discount_percentage: 'Discount percentage cannot exceed 100%' }));
      } else {
        dispatch(setErrors({ ...errors, [name]: '' }));
      }

      dispatch(setFormData({ [name]: numericValue }));
      return;
    }

    if (typeof value === 'string' && value.trim() === '') {
      dispatch(setErrors({
        ...errors,
        [name]: `${name.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())} is required`,
      }));
    } else {
      dispatch(setErrors({ ...errors, [name]: '' }));
    }

    dispatch(setFormData({ [name]: value }));
  };

  const validate = () => {
    const newErrors = {};
    requiredFields.forEach((field) => {
      const value = formData[field];

      if (
        value === null ||
        value === undefined ||
        (typeof value === 'string' && value.trim() === '') ||
        (typeof value === 'number' && isNaN(value))
      ) {
        newErrors[field] = `${field.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())} is required`;
      }
    });

    if (!sameAsAddress && !formData.delivery_address.trim()) {
      newErrors.delivery_address = 'Delivery address is required';
    }

    if (formData.email && !/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (formData.phone && !/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = 'Phone number must be 10 digits';
    }

    if (!selectedCustomerId) {
      newErrors.customer_id = 'Customer selection is required';
    }
    if (formData.order_date && formData.delivery_date && formData.order_date > formData.delivery_date) {
      newErrors.delivery_date = 'Delivery date must be after order date';
    }
    if (formData.payment_date && formData.order_date && formData.payment_date < formData.order_date) {
      newErrors.payment_date = 'Advance date must be on or after order date';
    }
    if (parseFloat(formData.packing_charges) < 0) {
      newErrors.packing_charges = 'Packing charges cannot be negative';
    }
    if (parseFloat(formData.discount_percentage) < 0) {
      newErrors.discount_percentage = 'Discount percentage cannot be negative';
    }
    if (parseFloat(formData.discount_percentage) > 100) {
      newErrors.discount_percentage = 'Discount percentage cannot exceed 100%';
    }
    if (invoiceStatus.exists === true) {
      newErrors.invoice_number = invoiceStatus.message || 'Invoice number already exists';
    }

    dispatch(setErrors(newErrors));
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid = () => {
    return (
      requiredFields.every((field) => String(formData[field] ?? '').trim()) &&
      (sameAsAddress || formData.delivery_address.trim()) &&
      cartItems.length > 0 &&
      selectedCustomerId &&
      invoiceStatus.exists === false
    );
  };

  const handleCustomer = async (customerFormData) => {
    try {
      dispatch(setLoading(true));
      const res = await createCustomer({
        name: customerFormData.name,
        phone: customerFormData.phone,
        address: customerFormData.address,
        contact_type: customerFormData.contact_type,
      });
      const newCustomer = res?.data?.data;

      if (!newCustomer?.id) {
        throw new Error("Invalid customer data returned from server");
      }

      showSnackbar("Customer added successfully!", "success");
      
      // First, close the modal
      dispatch(setOpenModal(false));
      
      // Re-fetch customers to update the list
      await fetchCustomers();

      // IMPORTANT: Use setTimeout to ensure the customers state is updated before selecting
      setTimeout(() => {
        // Update selected customer and form data
        dispatch(setSelectedCustomerId(newCustomer.id.toString()));
        dispatch(setSelectedCustomer(newCustomer));
        dispatch(setMode("existing"));
        dispatch(setFormData({
          ...formData,
          customer_id: newCustomer.id.toString(),
          name: newCustomer.name || "",
          phone: newCustomer.phone || "",
          address: newCustomer.address || "",
          contact_type: newCustomer.contact_type || "",
          delivery_address: sameAsAddress ? newCustomer.address || "" : formData.delivery_address,
        }));
        dispatch(setErrors({
          ...errors,
          customer_id: "",
          name: "",
          phone: "",
          address: "",
          contact_type: "",
        }));
      }, 100);
      
    } catch (error) {
      console.error("Error creating customer:", error);
      const errorMessage =
        error.response?.data?.errors?.phone?.[0] ||
        error.response?.data?.message ||
        "Failed to add customer!";
      dispatch(setErrors({ ...errors, phone: errorMessage }));
      showSnackbar(errorMessage, "error");
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    dispatch(setSubmitting(true));
    const discountAmount = parseFloat(formData.discount_percentage) || 0;

    const payload = {
      customer_id: selectedCustomerId,
      order_type: formData.order_type,
      branch_id: formData.branch_id,
      status: 'order_placed',
      order_date: formData.order_date,
      delivery_date: formData.delivery_date,
      delivery_address: formData.delivery_address,
      payment_method: formData.payment_method,
      advance_amount: parseFloat(formData.advance_amount) || 0,
      pending: parseFloat(formData.pending) || 0,
      invoice_number: formData.invoice_number,
      branch_code: formData.branch_code,
      payment_date: formData.payment_date,
      packing_charges: parseFloat(formData.packing_charges) || 0,
      salesman_name: formData.salesman_name,
      delivery_type: formData.delivery_type,
      notes: formData.notes,
      discount_percentage: parseFloat(formData.discount_percentage) || 0,
      customer: {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        gst_number: formData.gst_number || '',
        contact_type: formData.contact_type,
      },
      items: cartItems.map((item) => ({
        product_id: item.product_id || item.id,
        quantity: item.quantity,
        price: item.base_price,
      })),
      invoice_colors: {
        primary: invoicePrimaryColor,
        secondary: invoiceSecondaryColor,
      },
    };

    try {
      const res = await createOrder(payload);
      const order = res?.data?.data;
      if (order) {
        showSnackbar('Order created successfully!', 'success');
        localStorage.removeItem('selectedProducts');
        localStorage.setItem('showOrderSuccessToast', '1');
        localStorage.setItem('editOrderData', JSON.stringify(order));
        navigate('/order-confirm', { state: order });
      } else {
        showSnackbar('Invalid server response', 'error');
      }
    } catch (err) {
      console.error('Order submission failed:', err);
      if (err.response?.data?.errors) {
        const apiErrors = err.response.data.errors;
        Object.values(apiErrors).forEach((messages) => {
          if (Array.isArray(messages)) {
            messages.forEach((msg) => showSnackbar(msg, 'error'));
          } else {
            showSnackbar(messages, 'error');
          }
        });
      } else if (err.response?.data?.message) {
        showSnackbar(err.response?.data?.message, 'error');
      } else {
        showSnackbar('Order submission failed', 'error');
      }
    } finally {
      dispatch(setSubmitting(false));
    }
  };

  return (
    <Box
      mt={2}
      mb={{ xs: 8, sm: 6 }}
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        p: { xs: 1, sm: 0 },
        gap: { xs: 2, sm: 3, md: 4 },
        minHeight: '100vh',
      }}
    >
      <Box
        sx={{
          flex: { xs: '1 1 100%', md: '1 1 70%' },
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 2, sm: 3 },
          minWidth: 0,
        }}
      >
        <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, boxShadow: 'none' }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 500,
              mb: 2,
              fontSize: { xs: '1rem', sm: '1.1rem', md: '1.1rem' },
            }}
          >
            Invoice Details
          </Typography>
          <Grid container spacing={{ xs: 2, sm: 2 }}>
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: '13px' }}
              >
                Branch*
              </Typography>
              {roleId === '1' ? (
                <FormControl
                  fullWidth
                  size="small"
                  error={!!errors.branch_id}
                  sx={{
                    width: { xs: 280, sm: 300, md: 190, lg: 249 },
                    mb: 0,
                  }}
                  disabled={loading}
                >
                  <Select
                    name="branch_id"
                    value={formData.branch_id ?? ''}
                    onChange={handleBranchSelect}
                    displayEmpty
                    renderValue={(value) => {
                      if (!value) {
                        return <Box sx={{ color: 'text.secondary' }}>Select branch</Box>;
                      }
                      return branches.find((b) => b.id === Number(value))?.name || '';
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select branch
                    </MenuItem>
                    {branches.map((b) => (
                      <MenuItem key={b.id} value={b.id}>
                        {b.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.branch_id && <FormHelperText>{errors.branch_id}</FormHelperText>}
                </FormControl>
              ) : (
                <TextField
                  fullWidth
                  value={branchName}
                  disabled
                  variant="outlined"
                  InputLabelProps={{ shrink: false }}
                  sx={{
                    width: { xs: 280, sm: 300, md: 190, lg: 249 },
                    bgcolor: '#f1f1f1ee',
                    '& .MuiInputLabel-root': { display: 'none' },
                    mb: 0,
                  }}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography
                sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: '13px' }}
              >
                Branch Code
              </Typography>
              <TextField
                value={formData.branch_code || ''}
                placeholder="Code"
                fullWidth
                InputProps={{ readOnly: true }}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                sx={{
                  width: { xs: 280, sm: 300, md: 200, lg: 150 },
                  bgcolor: '#f1f1f1ee',
                  '& .MuiInputLabel-root': { display: 'none' },
                  mb: 0,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <Typography
                sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: '13px' }}
              >
                Invoice Number*
              </Typography>
              <TextField
                name="invoice_number"
                value={formData.invoice_number}
                onChange={handleChange}
                placeholder="invoice number"
                fullWidth
                error={!!errors.invoice_number}
                helperText={
                  errors.invoice_number ||
                  (invoiceStatus.exists === false ? invoiceStatus.message : '')
                }
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                sx={{
                  width: { xs: 280, sm: 300, md: 190, lg: 249 },
                  '& .MuiInputLabel-root': { display: 'none' },
                  mb: 0,
                }}
                InputProps={{
                  endAdornment: invoiceStatus.exists === false && (
                    <InputAdornment position="end">
                      <Typography color="green" sx={{ fontSize: '0.8rem' }}>
                        ✓
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                FormHelperTextProps={{
                  sx: { fontSize: '0.65rem', mt: 0.2 },
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        <Paper
          elevation={1}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            bgcolor: '#fff',
            boxShadow: 'none',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 500,
              mb: 3,
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
            }}
          >
            Customer Details*
          </Typography>
          <Grid container spacing={{ xs: 2, sm: 3 }} mb={2}>
            <Grid item xs={12}>
              <Typography
                sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: '13px' }}
              >
                Select Customer
              </Typography>
              <FormControl
                fullWidth
                size="small"
                error={!!errors.customer_id}
                sx={{
                  width: { xs: 280, sm: 300, md: 190, lg: 249 },
                  mb: 0,
                }}
                disabled={loading}
              >
                <Select
                  value={selectedCustomerId ?? ''}
                  onChange={handleCustomerSelect}
                  displayEmpty
                  renderValue={(value) => {
                    if (!value) {
                      return <Box sx={{ color: 'text.secondary' }}>Select customer</Box>;
                    }
                    if (value === 'new') {
                      return 'Add New Customer';
                    }
                    const customer = customers.find((c) => c.id === parseInt(value));
                    return customer?.name || '';
                  }}
                >
                  <MenuItem value="" disabled>
                    Select customer
                  </MenuItem>
                  <MenuItem value="new">
                    <Button sx={{ background: Color.primary.main, color: '#fff', mt: 1, ml: 2 }}>
                      + Add New Customer
                    </Button>
                  </MenuItem>
                  <Divider sx={{ border: '1px solid gray' }} />
                  {customers.map((cust) => (
                    <MenuItem key={cust.id} value={cust.id.toString()}>
                      {cust.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.customer_id && <FormHelperText>{errors.customer_id}</FormHelperText>}
              </FormControl>
            </Grid>
          </Grid>
          {selectedCustomer && (
            <>
              <Typography
                sx={{ mb: 0, fontWeight: 600, color: Color.label.main, fontSize: '13px' }}
              >
                Billing Address
              </Typography>
              <Paper
                elevation={3}
                sx={{
                  p: 2,
                  mt: 1,
                  mb: 1,
                  borderRadius: 3,
                  bgcolor: '#fff',
                  boxShadow: 'none',
                  border: '1px solid lightgray',
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' }, fontWeight: 600, mb: 0.5 }}
                    >
                      Name:
                      <Typography
                        component="span"
                        sx={{
                          fontWeight: 400,
                          ml: 1,
                          fontSize: { xs: '0.85rem', sm: '0.9rem' },
                        }}
                      >
                        {selectedCustomer.name || '-'}
                      </Typography>
                    </Typography>
                    <Typography
                      sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' }, fontWeight: 600, mb: 0.5 }}
                    >
                      Phone:
                      <Typography
                        component="span"
                        sx={{
                          fontWeight: 400,
                          ml: 1,
                          fontSize: { xs: '0.85rem', sm: '0.9rem' },
                        }}
                      >
                        {selectedCustomer.phone || '-'}
                      </Typography>
                    </Typography>
                    <Typography
                      sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' }, fontWeight: 600 }}
                    >
                      Address:
                      <Typography
                        component="span"
                        sx={{
                          fontWeight: 400,
                          ml: 1,
                          fontSize: { xs: '0.85rem', sm: '0.9rem' },
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {selectedCustomer.address || '-'}
                      </Typography>
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </>
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={sameAsAddress}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  dispatch(setSameAsAddress(isChecked));
                  if (isChecked) {
                    dispatch(setFormData({ delivery_address: formData.address }));
                    dispatch(setErrors({ ...errors, delivery_address: '' }));
                  }
                }}
                sx={{
                  color: Color.primary.main,
                  '&.Mui-checked': {
                    color: Color.primary.main,
                    fontSize: '20px',
                  },
                }}
              />
            }
            label="Delivery address Same as billing address"
            sx={{ fontSize: { xs: '0.85rem', sm: '0.8rem', md: '0.7rem' } }}
          />
          {!sameAsAddress && (
            <Grid container spacing={2}>
              <Grid item xs={12} mt={0.5}>
                <Typography
                  sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: '13px' }}
                >
                  Delivery Address*
                </Typography>
                <TextField
                  fullWidth
                  name="delivery_address"
                  value={formData.delivery_address}
                  onChange={handleChange}
                  placeholder="Enter delivery address"
                  multiline
                  rows={3}
                  error={!!errors.delivery_address}
                  helperText={errors.delivery_address}
                  variant="outlined"
                  InputLabelProps={{ shrink: false }}
                  sx={{
                    width: { xs: 280, sm: 350, md: 530, lg: 540 },
                    '& .MuiInputLabel-root': { display: 'none' },
                    mb: 0,
                  }}
                />
              </Grid>
            </Grid>
          )}
        </Paper>

        <Paper
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 3,
            bgcolor: 'white',
            boxShadow: 'none',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
              mb: 2,
              fontWeight: 500,
            }}
          >
            Order Type
          </Typography>
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <RadioGroup
              row
              name="order_type"
              value={formData.order_type}
              onChange={handleChange}
            >
              {ORDER_TYPE.map((type) => (
                <FormControlLabel
                  key={type.value}
                  value={type.value}
                  control={
                    <Radio
                      sx={{
                        '&.Mui-checked': {
                          color: Color.primary.main,
                        },
                      }}
                    />
                  }
                  label={type.label}
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: { xs: '0.85rem', sm: '0.9rem' },
                    },
                  }}
                />
              ))}
            </RadioGroup>
          </Grid>
        </Paper>

        <Paper
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 3,
            bgcolor: 'white',
            boxShadow: 'none',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
              mb: 3,
              fontWeight: 500,
            }}
          >
            Order Information
          </Typography>
          <Grid container spacing={{ xs: 2, sm: 2 }} mb={0}>
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: '13px' }}
              >
                Order Date*
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  value={formData.order_date}
                  open={openOrder}
                  onOpen={() => dispatch(setOpenOrder(true))}
                  onClose={() => dispatch(setOpenOrder(false))}
                  onChange={(newValue) => {
                    dispatch(setFormData({
                      order_date: newValue,
                      delivery_date:
                        formData.delivery_date && newValue && formData.delivery_date < newValue
                          ? null
                          : formData.delivery_date,
                      payment_date:
                        formData.payment_date && newValue && formData.payment_date < newValue
                          ? null
                          : formData.payment_date,
                    }));
                    if (!newValue) {
                      dispatch(setErrors({ ...errors, order_date: 'Order date is required' }));
                    } else {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const maxDate = new Date();
                      maxDate.setFullYear(today.getFullYear() + 1);
                      if (newValue < today) {
                        dispatch(setErrors({
                          ...errors,
                          order_date: 'Order date cannot be in the past',
                        }));
                      } else if (newValue > maxDate) {
                        dispatch(setErrors({
                          ...errors,
                          order_date: 'Order date cannot be more than one year in the future',
                        }));
                      } else {
                        dispatch(setErrors({ ...errors, order_date: '' }));
                        dispatch(setOpenOrder(false));
                      }
                    }
                  }}
                  format="dd-MM-yyyy"
                  minDate={new Date()}
                  maxDate={new Date(new Date().setFullYear(new Date().getFullYear() + 1))}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.order_date,
                      helperText: errors.order_date,
                      onClick: () => dispatch(setOpenOrder(true)),
                      placeholder: 'Select order date',
                      InputLabelProps: { shrink: false },
                      size: 'small',
                      sx: {
                        width: { xs: 280, sm: 300, md: 280, lg: 249 },
                        '& .MuiInputLabel-root': { display: 'none' },
                        mb: 0,
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: '13px' }}
              >
                Delivery Date*
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  value={formData.delivery_date}
                  open={openDelivery}
                  onOpen={() => dispatch(setOpenDelivery(true))}
                  onClose={() => dispatch(setOpenDelivery(false))}
                  onChange={(newValue) => {
                    dispatch(setFormData({ delivery_date: newValue }));
                    if (!newValue) {
                      dispatch(setErrors({ ...errors, delivery_date: 'Delivery date is required' }));
                    } else {
                      const maxDate = new Date();
                      maxDate.setFullYear(maxDate.getFullYear() + 1);
                      const orderDate = formData.order_date || new Date();
                      orderDate.setHours(0, 0, 0, 0);
                      const newValueDate = new Date(newValue);
                      newValueDate.setHours(0, 0, 0, 0);
                      if (newValueDate < orderDate) {
                        dispatch(setErrors({
                          ...errors,
                          delivery_date: 'Delivery date must be on or after order date',
                        }));
                      } else if (newValueDate > maxDate) {
                        dispatch(setErrors({
                          ...errors,
                          delivery_date: 'Delivery date cannot be more than one year in the future',
                        }));
                      } else {
                        dispatch(setErrors({ ...errors, delivery_date: '' }));
                        dispatch(setOpenDelivery(false));
                      }
                    }
                  }}
                  format="dd-MM-yyyy"
                  minDate={formData.order_date || new Date()}
                  maxDate={new Date(new Date().setFullYear(new Date().getFullYear() + 1))}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.delivery_date,
                      helperText: errors.delivery_date || '',
                      onClick: () => dispatch(setOpenDelivery(true)),
                      placeholder: 'Select delivery date',
                      InputLabelProps: { shrink: false },
                      size: 'small',
                      sx: {
                        width: { xs: 280, sm: 300, md: 190, lg: 249 },
                        '& .MuiInputLabel-root': { display: 'none' },
                        mb: 0,
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: '13px' }}
              >
                Salesman Name*
              </Typography>
              <TextField
                name="salesman_name"
                value={formData.salesman_name}
                onChange={handleChange}
                placeholder="Enter salesman name"
                fullWidth
                error={!!errors.salesman_name}
                helperText={errors.salesman_name}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                sx={{
                  width: { xs: 280, sm: 300, md: 190, lg: 249 },
                  '& .MuiInputLabel-root': { display: 'none' },
                  mb: 0,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: '13px' }}
              >
                Delivery Type*
              </Typography>
              <FormControl
                fullWidth
                size="small"
                error={!!errors.delivery_type}
                sx={{
                  width: { xs: 280, sm: 300, md: 190, lg: 249 },
                  mb: 0,
                }}
              >
                <Select
                  name="delivery_type"
                  value={formData.delivery_type ?? ''}
                  onChange={handleChange}
                  displayEmpty
                  renderValue={(value) => {
                    if (!value) {
                      return <Box sx={{ color: 'text.secondary' }}>Select delivery type</Box>;
                    }
                    return DELIVERY_TYPE.find((s) => s.value === value)?.label || '';
                  }}
                >
                  <MenuItem value="" disabled>
                    Select delivery type
                  </MenuItem>
                  {DELIVERY_TYPE.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.delivery_type && <FormHelperText>{errors.delivery_type}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: '13px' }}
              >
                Packing Charges*
              </Typography>
              <TextField
                name="packing_charges"
                type="number"
                value={formData.packing_charges}
                onChange={handleChange}
                placeholder="Enter packing charges"
                fullWidth
                error={!!errors.packing_charges}
                helperText={errors.packing_charges}
                inputProps={{ min: 0 }}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                sx={{
                  width: { xs: 280, sm: 300, md: 190, lg: 249 },
                  '& .MuiInputLabel-root': { display: 'none' },
                  mb: 2,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: '13px' }}
              >
                Discount (%)
              </Typography>
              <TextField
                name="discount_percentage"
                type="number"
                value={formData.discount_percentage}
                onChange={handleChange}
                placeholder="Enter discount percentage"
                fullWidth
                error={!!errors.discount_percentage}
                helperText={errors.discount_percentage}
                inputProps={{ min: 0, max: 100 }}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                sx={{
                  width: { xs: 280, sm: 300, md: 190, lg: 249 },
                  '& .MuiInputLabel-root': { display: 'none' },
                  mb: 1,
                }}
              />
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12}>
            <Typography
              sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: '13px' }}
            >
              Notes (optional)
            </Typography>
            <TextField
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Enter notes"
              fullWidth
              multiline
              rows={2}
              variant="outlined"
              InputLabelProps={{ shrink: false }}
              sx={{
                width: { xs: 280, sm: 300, md: 600, lg: 515 },
                gridColumn: { sm: 'span 2' },
                '& .MuiInputLabel-root': { display: 'none' },
                mb: 0,
              }}
            />
          </Grid>
          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
              mt: 4,
              mb: 2,
              fontWeight: 500,
            }}
          >
            Payment Information
          </Typography>
          <Grid container spacing={{ xs: 2, sm: 2 }} mb={1}>
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: '13px' }}
              >
                Payment Method*
              </Typography>
              <FormControl
                fullWidth
                size="small"
                error={!!errors.payment_method}
                sx={{
                  width: { xs: 280, sm: 300, md: 190, lg: 249 },
                  mb: 0,
                }}
              >
                <Select
                  name="payment_method"
                  value={formData.payment_method ?? ''}
                  onChange={handleChange}
                  displayEmpty
                  renderValue={(value) => {
                    if (!value) {
                      return <Box sx={{ color: 'text.secondary' }}>Select payment method</Box>;
                    }
                    return paymentMethods.find((s) => s.value === value)?.label || '';
                  }}
                >
                  <MenuItem value="" disabled>
                    Select payment method
                  </MenuItem>
                  {paymentMethods.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.payment_method && <FormHelperText>{errors.payment_method}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: '13px' }}
              >
                Total
              </Typography>
              <TextField
                value={totals.total !== undefined ? totals.total.toFixed(0) : '0'}
                InputProps={{ readOnly: true }}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                sx={{
                  width: { xs: 280, sm: 300, md: 190, lg: 249 },
                  bgcolor: '#f1f1f1ee',
                  '& .MuiInputLabel-root': { display: 'none' },
                  mb: 1,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: '13px' }}
              >
                Advance* (
                <span style={{ fontWeight: 400, color: Color.label.main }}>
                  {formData.payment_date
                    ? formData.payment_date.toLocaleDateString('en-GB')
                    : errors.payment_date
                    ? errors.payment_date
                    : 'Advance date'}
                </span>
                )
              </Typography>
              <TextField
                type="number"
                name="advance_amount"
                value={formData.advance_amount}
                onChange={handleChange}
                placeholder="Enter advance"
                error={!!errors.advance_amount}
                helperText={errors.advance_amount}
                inputProps={{ min: 0 }}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                sx={{
                  width: { xs: 280, sm: 300, md: 190, lg: 249 },
                  '& .MuiInputLabel-root': { display: 'none' },
                  mb: 1,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <IconButton
                            sx={{ p: 1 }}
                            ref={iconRef}
                            onClick={() => dispatch(setOpenAdvance(true))}
                          >
                            <CalendarTodayIcon sx={{ fontSize: '20px', color: Color.primary.main }} />
                          </IconButton>
                        </Stack>
                        <DatePicker
                          open={openAdvance}
                          onClose={() => dispatch(setOpenAdvance(false))}
                          value={formData.payment_date}
                          onChange={(newValue) => {
                            dispatch(setFormData({ payment_date: newValue }));
                            if (!newValue) {
                              dispatch(setErrors({
                                ...errors,
                                payment_date: 'Advance date is required',
                              }));
                            } else if (formData.order_date && newValue < formData.order_date) {
                              dispatch(setErrors({
                                ...errors,
                                payment_date: 'Advance date must be on or after order date',
                              }));
                            } else {
                              dispatch(setErrors({ ...errors, payment_date: '' }));
                            }
                          }}
                          minDate={new Date()}
                          slots={{
                            textField: () => null,
                          }}
                          slotProps={{
                            popper: {
                              anchorEl: iconRef.current,
                              placement: 'bottom-start',
                            },
                          }}
                          enableAccessibleFieldDOMStructure={false}
                        />
                      </LocalizationProvider>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: '13px' }}
              >
                Pending
              </Typography>
              <TextField
                value={formData.pending || '0.00'}
                InputProps={{ readOnly: true }}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                sx={{
                  width: { xs: 280, sm: 300, md: 190, lg: 249 },
                  bgcolor: '#f1f1f1ee',
                  '& .MuiInputLabel-root': { display: 'none' },
                  mb: 0,
                }}
              />
            </Grid>
          </Grid>
        </Paper>
      </Box>

      <Box
        sx={{
          flex: { xs: '1 1 100%', md: '1 1 30%' },
          position: { md: 'sticky', xs: 'static' },
          top: { md: 17 },
          alignSelf: { md: 'flex-start' },
          height: { xs: 'auto', sm: '70vh', md: '81vh' },
        }}
      >
        <Paper
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 500,
              mb: 2,
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
            }}
          >
            Order Details
          </Typography>
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              pr: { xs: 0, sm: 1 },
              maxHeight: { xs: 'auto', sm: 'calc(70vh - 200px)', md: 'calc(100vh - 200px)' },
            }}
          >
            {cartItems.map((item) => {
              const price = parseFloat(item.base_price || 0);
              const gst = parseFloat(item.tax_rate || 0);
              const unitPriceWithTax = price + (price * gst) / 100;
              const taxAmount = (price * gst) / 100;
              const total = unitPriceWithTax * item.quantity;
              return (
                <Box
                  key={item.id}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    py: 2,
                    borderBottom: '1px solid #f0f0f0',
                    flexWrap: 'wrap',
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 120 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        fontSize: { xs: '0.8rem', sm: '0.85rem' },
                      }}
                    >
                      {item.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    >
                      ₹{unitPriceWithTax.toFixed(2)} × {item.quantity} (Tax: ₹{taxAmount.toFixed(2)})
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        fontSize: { xs: '0.8rem', sm: '0.85rem' },
                      }}
                    >
                      ₹{total.toFixed(2)}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        onClick={() => dispatch(updateQuantity({ id: item.id, change: -1 }))}
                        size="small"
                      >
                        <Remove fontSize="small" />
                      </IconButton>
                      <IconButton
                        onClick={() => dispatch(updateQuantity({ id: item.id, change: 1 }))}
                        size="small"
                      >
                        <Add fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              );
            })}
            {cartItems.length === 0 && (
              <Typography
                variant="body2"
                color="error"
                textAlign="center"
                py={4}
                sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}
              >
                No items in cart.
              </Typography>
            )}
          </Box>
          <Box mt={2} pt={2} borderTop="1px solid #eee">
            <Typography color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
              Subtotal: ₹{(totals.subtotal || 0).toFixed(2)}
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
              Tax Amount: ₹{(totals.taxAmount || 0).toFixed(2)}
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
              Packing Charges: ₹{(parseFloat(formData.packing_charges) || 0).toFixed(2)}
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
              Discount ({formData.discount_percentage || 0}%)
            </Typography>
            <Typography
              fontWeight={600}
              mt={1}
              sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' } }}
            >
              Total: ₹{(totals.total || 0).toFixed(2)}
            </Typography>
            <Box mt={3}>
              <Button
                fullWidth
                color="primary"
                onClick={handleSubmit}
                disabled={!isFormValid() || submitting}
                sx={{
                  py: 1.3,
                  borderRadius: 2,
                  fontWeight: 600,
                  background: Color.primary.main,
                  color: 'white',
                  '&:disabled': {
                    background: '#7e7a7aff',
                    color: '#f3f0f0ff',
                    cursor: 'not-allowed',
                    pointerEvents: 'auto',
                  },
                  fontSize: { xs: '0.85rem', sm: '0.9rem' },
                }}
              >
                {submitting ? 'Creating...' : 'Create Order'}
              </Button>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => navigate('/create-order')}
                sx={{
                  mt: 2,
                  py: 1.3,
                  borderRadius: 2,
                  borderColor: Color.secondary.main,
                  color: Color.secondary.main,
                  fontSize: { xs: '0.85rem', sm: '0.9rem' },
                  '&:hover': {
                    borderColor: Color.secondary.dark,
                    color: Color.secondary.dark,
                    backgroundColor: 'transparent',
                  },
                }}
              >
                Back to Products
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
      <AddCustomer
        open={openModal}
        onClose={() => dispatch(setOpenModal(false))}
        onSubmit={handleCustomer}
        formData={formData}
        setFormData={(data) => dispatch(setFormData(data))}
        errors={errors}
        setErrors={(err) => dispatch(setErrors(err))}
      />
    </Box>
  );
};

export default OrderDetails;


// old code 

// import React, { useEffect, useState, useRef } from 'react';
// import {
//   Box,
//   Typography,
//   Grid,
//   Paper,
//   Stack,
//   TextField,
//   MenuItem,
//   IconButton,
//   InputAdornment,
//   Button,
//   Radio,
//   RadioGroup,
//   FormControlLabel,
//   Checkbox,
//   FormControl,
//   InputLabel,
//   Select,
//   FormHelperText,
//   Divider,
// } from "@mui/material";
// import {
//   Add,
//   Remove,
//   ArrowBack,
// } from "@mui/icons-material";
// import { useNavigate } from "react-router-dom";
// import { useSnackbar } from "../../App"; 
// import { createOrder, checkInvoiceNumber } from "../../service/orderService";
// import { getAllBranchLists } from "../../service/branchService";
// import { createCustomer, getAllCustomers } from "../../service/customerService";
// import AddCustomer from "../Customer/AddCustomer";
// import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
// import { DatePicker } from "@mui/x-date-pickers/DatePicker";
// import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
// import AppButton from "../../components/AppButton";
// import Color from "../../components/Color";
// import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
// import debounce from "lodash.debounce";

// const ORDER_TYPE = [
//   { label: "Quotation", value: "quotation" },
//   { label: "Invoice", value: "invoice" },
// ];

// const DELIVERY_TYPE = [
//   { label: "Home Delivery", value: "home_delivery" },
//   { label: "PickUp", value: "pickup" },
// ];

// const requiredFields = [
//   "name",
//   "phone",
//   "address",
//   "order_date",
//   "delivery_date",
//   "payment_method",
//   "order_type",
//   "branch_id",
//   "packing_charges",
//   "salesman_name",
//   "delivery_type",
//   "payment_date",
//   "status",
// ];

// const OrderDetails = () => {
//   const { showSnackbar } = useSnackbar();
//   const [openAdvance, setOpenAdvance] = useState(false);
//   const iconRef = useRef(null);
//   const navigate = useNavigate();
//   const [branches, setBranches] = useState([]);
//   const [cartItems, setCartItems] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [formData, setFormData] = useState({
//     name: "",
//     email: "",
//     phone: "",
//     address: "",
//     gst_number: "",
//     contact_type: "",
//     order_date: new Date(),
//     delivery_date: null,
//     advance_amount: "",
//     pending: "",
//     payment_method: "",
//     order_type: "quotation",
//     branch_id: "",
//     delivery_address: "",
//     packing_charges: "",
//     salesman_name: "",
//     discount_amount: "",
//     delivery_type: "",
//     payment_date: new Date(),
//     invoice_number: "",
//     branch_code: "",
//     notes: "",
//     status: "order_placed",
//   });
//   const [errors, setErrors] = useState({});
//   const [sameAsAddress, setSameAsAddress] = useState(true);
//   const [customers, setCustomers] = useState([]);
//   const [mode, setMode] = useState("");
//   const [selectedCustomerId, setSelectedCustomerId] = useState("");
//   const [openModal, setOpenModal] = useState(false);
//   const [selectedCustomer, setSelectedCustomer] = useState(null);
//   const [submitting, setSubmitting] = useState(false);
//   const [userRole, setUserRole] = useState("");
//   const [userBranch, setUserBranch] = useState("");
//   const [totals, setTotals] = useState({ subtotal: 0, total: 0 });
//   const [invoiceStatus, setInvoiceStatus] = useState({ exists: null, message: "" });
//   const [openOrder, setOpenOrder] = useState(false);
//   const [openDelivery, setOpenDelivery] = useState(false);

//   const settings = JSON.parse(localStorage.getItem("settings") || "{}");
//   const invoicePrimaryColor = settings.invoice_primary_color || "#e60073";
//   const invoiceSecondaryColor = settings.invoice_secondary_color || "#f2c0d7";

//   const paymentMethods = [
//     { label: "Cash", value: "cash" },
//     { label: "UPI", value: "upi" },
//     { label: "Card", value: "card" },
//     { label: "Bank Transfer", value: "bank_transfer" },
//   ];

//   const fetchCustomers = async () => {
//     setLoading(true);
//     try {
//       const res = await getAllCustomers();
//       const data = res?.data;
//       if (Array.isArray(data?.data)) {
//         setCustomers(data.data);
//       } else {
//         setCustomers([]);
//         showSnackbar("Unexpected response format!", "error");
//       }
//     } catch (error) {
//       showSnackbar("Failed to fetch customers!", "error");
//       setCustomers([]);
//     }
//     setLoading(false);
//   };

//   const fetchBranches = async () => {
//     setLoading(true);
//     try {
//       const res = await getAllBranchLists();
//       const list = res?.data?.data || [];
//       setBranches(Array.isArray(list) ? list : []);
//     } catch (error) {
//       showSnackbar("Failed to fetch branches!", "error");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const checkInvoiceNumberDebounced = debounce(async (invoiceNumber) => {
//     if (!invoiceNumber.trim()) {
//       setErrors((prev) => ({
//         ...prev,
//         invoice_number: "Invoice Number is required",
//       }));
//       setInvoiceStatus({ exists: null, message: "" });
//       return;
//     }

//     try {
//       const response = await checkInvoiceNumber({ invoice_number: invoiceNumber });
//       const { exists, message } = response.data;
//       setInvoiceStatus({ exists, message });
//       setErrors((prev) => ({
//         ...prev,
//         invoice_number: exists ? message : "",
//       }));
//     } catch (error) {
//       console.error("Error checking invoice number:", error);
//       setInvoiceStatus({ exists: null, message: "" });
//       setErrors((prev) => ({
//         ...prev,
//         invoice_number: "Error checking invoice number",
//       }));
//     }
//   }, 500);

//   const handleBranchSelect = (e) => {
//     const { name, value } = e.target;
//     setFormData((prev) => ({ ...prev, [name]: value }));
//     const selected = branches.find((b) => b.id === Number(value));
//     if (selected) {
//       setFormData((prev) => ({
//         ...prev,
//         branch_code: selected.code,
//       }));
//     }
//     if (!value) {
//       setErrors((prev) => ({
//         ...prev,
//         branch_id: "Branch is required",
//       }));
//     } else {
//       setErrors((prev) => ({ ...prev, branch_id: "" }));
//     }
//   };

//   const handleCustomerSelect = (e) => {
//     const value = e.target.value;

//     if (value === "new") {
//       setMode("new");
//       setOpenModal(true);
//       setSelectedCustomer(null);
//       setFormData({
//         name: "",
//         phone: "",
//         email: "",
//         gst_number: "",
//         address: "",
//         branch_id: "",
//         order_date: null,
//         delivery_date: null,
//         contact_type: "",
//         delivery_address: "",
//         advance_amount: "",
//         pending: "",
//         payment_method: "",
//         order_type: "quotation",
//         payment_date: null,
//         discount_amount: "",
//         packing_charges: "",
//         salesman_name: "",
//         delivery_type: "",
//         invoice_number: "",
//         branch_code: "",
//         notes: "",
//         status: "order_placed",
//       });
//       setSelectedCustomerId("");
//       setErrors((prev) => ({ ...prev, customer_id: "Customer selection is required" }));
//     } else {
//       const selected = customers.find((c) => c.id === parseInt(value));
//       setSelectedCustomerId(value);
//       setSelectedCustomer(selected);
//       setMode("existing");

//       if (selected) {
//         setFormData((prev) => ({
//           ...prev,
//           ...selected,
//           contact_type: selected.contact_type || "",
//           delivery_address: sameAsAddress ? selected.address : selected.delivery_address || "",
//         }));
//         setErrors((prev) => ({ ...prev, customer_id: "" }));
//       }
//     }
//   };

//   useEffect(() => {
//     fetchBranches();
//     fetchCustomers();

//     const storedProducts = localStorage.getItem("selectedProducts");
//     if (storedProducts) setCartItems(JSON.parse(storedProducts));

//     // const role = localStorage.getItem("role");
//     // const branch = localStorage.getItem("branch");

//     // setUserRole(role);
//     // setUserBranch(branch);

//     // if (role !== "admin") {
//     //   setFormData((prev) => ({
//     //     ...prev,
//     //     branch_id: branch || "",
//     //   }));
//     // }
//   }, []);

//   useEffect(() => {
//     const newTotals = cartItems.reduce(
//       (acc, item) => {
//         const price = parseFloat(item.base_price || 0);
//         acc.subtotal += price * item.quantity;
//         return acc;
//       },
//       { subtotal: 0, total: 0 }
//     );

//     const packingCharges = parseFloat(formData.packing_charges) || 0;
//     const discountAmount = parseFloat(formData.discount_amount) || 0;
//     const discount = (newTotals.subtotal * discountAmount) / 100;
//     const newTotal = newTotals.subtotal + packingCharges - discount;
//     newTotals.total = newTotal > 0 ? Math.round(newTotal) : 0;

//     const adv = parseFloat(formData.advance_amount) || 0;
//     const pending = newTotals.total - adv;

//     setTotals(newTotals);
//     if (!isNaN(pending)) {
//       setFormData((prev) => ({ ...prev, pending: pending.toFixed(2) }));
//     }
//   }, [formData.packing_charges, formData.discount_amount, formData.advance_amount, cartItems]);

//   useEffect(() => {
//     if (sameAsAddress) {
//       setFormData((prev) => ({
//         ...prev,
//         delivery_address: prev.address || "",
//       }));
//     }
//   }, [sameAsAddress, formData.address]);

//   const handleChange = (e) => {
//     const { name, value } = e.target;

//     if (name === "invoice_number") {
//       setFormData((prev) => ({ ...prev, [name]: value }));
//       checkInvoiceNumberDebounced(value);
//       return;
//     }

//     if (name === "advance_amount" || name === "packing_charges" || name === "discount_amount") {
//       const numericValue = value === "" ? "" : Number(value);

//       if (numericValue < 0) {
//         setErrors((prev) => ({
//           ...prev,
//           [name]: `${name.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())} cannot be negative`,
//         }));
//       } else if (name === "advance_amount" && totals.total && numericValue > totals.total) {
//         setErrors((prev) => ({
//           ...prev,
//           advance_amount: "Advance cannot be more than total amount",
//         }));
//       } else if (name === "discount_amount" && numericValue > 100) {
//         setErrors((prev) => ({
//           ...prev,
//           discount_amount: "Discount percentage cannot exceed 100%",
//         }));
//       } else {
//         setErrors((prev) => ({ ...prev, [name]: "" }));
//       }

//       setFormData((prev) => ({ ...prev, [name]: numericValue }));
//       return;
//     }

//     if (typeof value === "string" && value.trim() === "") {
//       setErrors((prev) => ({
//         ...prev,
//         [name]: `${name.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())} is required`,
//       }));
//     } else {
//       setErrors((prev) => ({ ...prev, [name]: "" }));
//     }

//     setFormData((prev) => ({
//       ...prev,
//       [name]: value,
//     }));
//   };

//   const resetForm = () => {
//     setOpenModal(false);
//   };

//   const updateQuantity = (id, change) => {
//     const updated = cartItems
//       .map((item) =>
//         item.id === id
//           ? { ...item, quantity: Math.max(1, item.quantity + change) }
//           : item
//       )
//       .filter((item) => item.quantity > 0);

//     setCartItems(updated);
//     localStorage.setItem("selectedProducts", JSON.stringify(updated));
//   };

//   const validate = () => {
//     const newErrors = {};
//     requiredFields.forEach((field) => {
//       const value = formData[field];

//       if (
//         value === null ||
//         value === undefined ||
//         (typeof value === "string" && value.trim() === "") ||
//         (typeof value === "number" && isNaN(value))
//       ) {
//         newErrors[field] = `${field.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())} is required`;
//       }
//     });

//     if (!sameAsAddress && !formData.delivery_address.trim()) {
//       newErrors.delivery_address = "Delivery address is required";
//     }

//     if (formData.email && !/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(formData.email)) {
//       newErrors.email = "Invalid email format";
//     }
//     if (formData.phone && !/^\d{10}$/.test(formData.phone)) {
//       newErrors.phone = "Phone number must be 10 digits";
//     }

//     if (!selectedCustomerId) {
//       newErrors.customer_id = "Customer selection is required";
//     }
//     if (formData.order_date && formData.delivery_date && formData.order_date > formData.delivery_date) {
//       newErrors.delivery_date = "Delivery date must be after order date";
//     }
//     if (formData.payment_date && formData.order_date && formData.payment_date < formData.order_date) {
//       newErrors.payment_date = "Advance date must be on or after order date";
//     }
//     if (parseFloat(formData.packing_charges) < 0) {
//       newErrors.packing_charges = "Packing charges cannot be negative";
//     }
//     if (parseFloat(formData.discount_amount) < 0) {
//       newErrors.discount_amount = "Discount percentage cannot be negative";
//     }
//     if (parseFloat(formData.discount_amount) > 100) {
//       newErrors.discount_amount = "Discount percentage cannot exceed 100%";
//     }
//     if (invoiceStatus.exists === true) {
//       newErrors.invoice_number = invoiceStatus.message || "Invoice number already exists";
//     }

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   const isFormValid = () => {
//     return (
//       requiredFields.every((field) => String(formData[field] ?? "").trim()) &&
//       (sameAsAddress || formData.delivery_address.trim()) &&
//       cartItems.length > 0 &&
//       selectedCustomerId &&
//       invoiceStatus.exists === false
//     );
//   };

//   const handleCustomer = async () => {
//     try {
//       const res = await createCustomer(formData);
//       const newCustomer = res?.data?.data;
//       showSnackbar("Customer added successfully!", "success");
//       if (newCustomer?.id) {
//         await fetchCustomers();
//         setSelectedCustomerId(newCustomer.id.toString());
//         setSelectedCustomer(newCustomer);
//         setMode("existing");
//         setFormData((prev) => ({
//           ...prev,
//           customer_id: newCustomer.id.toString(),
//           name: newCustomer.name || "",
//           email: newCustomer.email || "",
//           phone: newCustomer.phone || "",
//           address: newCustomer.address || "",
//           gst_number: newCustomer.gst_number || "",
//           contact_type: newCustomer.contact_type || "",
//           delivery_address: sameAsAddress ? newCustomer.address || "" : prev.delivery_address,
//         }));
//         setErrors((prev) => ({
//           ...prev,
//           customer_id: "",
//           name: "",
//           email: "",
//           phone: "",
//           address: "",
//           contact_type: "",
//         }));
//       }
//       setOpenModal(false);
//     } catch (error) {
//       showSnackbar("Something went wrong!", "error");
//     }
//   };

//   const handleSubmit = async () => {
//     if (!validate()) return;
//     setSubmitting(true);
//     const discountAmount = parseFloat(formData.discount_amount) || 0;
//     const discount = (totals.subtotal * discountAmount) / 100;

//     const payload = {
//       customer_id: selectedCustomerId,
//       order_type: formData.order_type,
//       branch_id: formData.branch_id,
//       status: "order_placed",
//       order_date: formData.order_date,
//       delivery_date: formData.delivery_date,
//       delivery_address: formData.delivery_address,
//       payment_method: formData.payment_method,
//       advance_amount: parseFloat(formData.advance_amount) || 0,
//       pending: parseFloat(formData.pending) || 0,
//       invoice_number: formData.invoice_number,
//       branch_code: formData.branch_code,
//       payment_date: formData.payment_date,
//       packing_charges: parseFloat(formData.packing_charges) || 0,
//       salesman_name: formData.salesman_name,
//       delivery_type: formData.delivery_type,
//       notes: formData.notes,
//       customer: {
//         name: formData.name,
//         email: formData.email,
//         phone: formData.phone,
//         address: formData.address,
//         gst_number: formData.gst_number || "",
//         contact_type: formData.contact_type,
//       },
//       items: cartItems.map((item) => ({
//         product_id: item.product_id || item.id,
//         quantity: item.quantity,
//         price: item.base_price,
//         discount_amount: parseFloat(formData.discount_amount) || 0,
//       })),
//       invoice_colors: {
//         primary: invoicePrimaryColor,
//         secondary: invoiceSecondaryColor,
//       },
//     };

//     try {
//       const res = await createOrder(payload);
//       const order = res?.data?.data;
//       if (order) {
//         showSnackbar("Order created successfully!", "success");
//         localStorage.removeItem("selectedProducts");
//         localStorage.setItem("showOrderSuccessToast", "1");
//         localStorage.setItem("editOrderData", JSON.stringify(order));
//         navigate("/order-confirm", { state: order });
//       } else {
//         showSnackbar("Invalid server response", "error");
//       }
//     } catch (err) {
//       console.error("Order submission failed:", err);
//       if (err.response?.data?.errors) {
//         const apiErrors = err.response.data.errors;
//         Object.values(apiErrors).forEach((messages) => {
//           if (Array.isArray(messages)) {
//             messages.forEach((msg) => showSnackbar(msg, "error"));
//           } else {
//             showSnackbar(messages, "error");
//           }
//         });
//       } else if (err.response?.data?.message) {
//         showSnackbar(err.response?.data?.message, "error");
//       } else {
//         showSnackbar("Order submission failed", "error");
//       }
//     } finally {
//       setSubmitting(false);
//     }
//   };

//   return (
//     <Box
//       mt={2}
//       mb={{ xs: 8, sm: 6 }}
//       sx={{
//         display: "flex",
//         flexDirection: { xs: "column", md: "row" },
//         p: { xs: 1, sm: 0 },
//         gap: { xs: 2, sm: 3, md: 4 },
//         minHeight: "100vh",
//       }}
//     >
//       <Box
//         sx={{
//           flex: { xs: "1 1 100%", md: "1 1 70%" },
//           display: "flex",
//           flexDirection: "column",
//           gap: { xs: 2, sm: 3 },
//           minWidth: 0,
//         }}
//       >
//         <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, boxShadow: "none", }}>
//           <Typography
//             variant="h6"
//             sx={{
//               fontWeight: 500,
//               mb: 2,
//               fontSize: { xs: "1rem", sm: "1.1rem", md: "1.1rem" },
//             }}
//           >
//             Invoice Details
//           </Typography>
//           <Grid container spacing={{ xs: 2, sm: 2 }}>
//             <Grid item xs={12} sm={6}>
//               <Typography
//                 sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: "13px" }}
//               >
//                 Branch*
//               </Typography>
//               <FormControl
//                 fullWidth
//                 size="small"
//                 error={!!errors.branch_id}
//                 sx={{
//                   width: { xs: 280, sm: 215, md: 230 },
//                   mb: 0,
//                 }}
//                 disabled={loading}
//               >
//                 <Select
//                   name="branch_id"
//                   value={formData.branch_id ?? ""}
//                   onChange={handleBranchSelect}
//                   displayEmpty
//                   renderValue={(value) => {
//                     if (!value) {
//                       return (
//                         <Box sx={{ color: "text.secondary", }}>
//                           Select branch
//                         </Box>
//                       );
//                     }
//                     return branches.find((b) => b.id === Number(value))?.name || "";
//                   }}
//                 >
//                   <MenuItem value="" disabled>
//                     Select branch
//                   </MenuItem>
//                   {branches.map((b) => (
//                     <MenuItem key={b.id} value={b.id}>
//                       {b.name}
//                     </MenuItem>
//                   ))}
//                 </Select>
//                 {errors.branch_id && <FormHelperText>{errors.branch_id}</FormHelperText>}
//               </FormControl>
//             </Grid>
//             <Grid item xs={12} sm={4}>
//               <Typography
//                 sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: "13px" }}
//               >
//                 Branch Code
//               </Typography>
//               <TextField
//                 value={formData.branch_code || ""}
//                 placeholder="Code"
//                 fullWidth
//                 InputProps={{ readOnly: true }}
//                 variant="outlined"
//                 InputLabelProps={{ shrink: false }}
//                 sx={{
//                   width: { xs: 280, sm: 215, md: 100 },
//                   bgcolor: "#f1f1f1ee",
//                   "& .MuiInputLabel-root": { display: "none" },
//                   mb: 0,
//                 }}
//               />
//             </Grid>
//             <Grid item xs={12} sm={8}>
//               <Typography
//                 sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: "13px" }}
//               >
//                 Invoice Number*
//               </Typography>
//               <TextField
//                 name="invoice_number"
//                 value={formData.invoice_number}
//                 onChange={handleChange}
//                 placeholder="invoice number"
//                 fullWidth
//                 error={!!errors.invoice_number}
//                 helperText={
//                   errors.invoice_number ||
//                   (invoiceStatus.exists === false ? invoiceStatus.message : "")
//                 }
//                 variant="outlined"
//                 InputLabelProps={{ shrink: false }}
//                 sx={{
//                   width: { xs: 280, sm: 215, md: 180 },
//                   "& .MuiInputLabel-root": { display: "none" },
//                   mb: 0,
//                 }}
//                 InputProps={{
//                   endAdornment: invoiceStatus.exists === false && (
//                     <InputAdornment position="end">
//                       <Typography color="green" sx={{ fontSize: "0.8rem" }}>
//                         ✓
//                       </Typography>
//                     </InputAdornment>
//                   ),
//                 }}
//                 FormHelperTextProps={{
//                   sx: { fontSize: "0.65rem", mt: 0.2 },
//                 }}
//               />
//             </Grid>
//           </Grid>
//         </Paper>

//         <Paper
//           elevation={1}
//           sx={{
//             p: { xs: 2, sm: 3 },
//             borderRadius: 2,
//             bgcolor: "#fff",
//             boxShadow: "none",
//           }}
//         >
//           <Typography
//             variant="h6"
//             sx={{
//               fontWeight: 500,
//               mb: 3,
//               fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
//             }}
//           >
//             Customer Details*
//           </Typography>
//           <Grid container spacing={{ xs: 2, sm: 3 }} mb={2}>
//             <Grid item xs={12}>
//               <Typography
//                 sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: "13px" }}
//               >
//                 Select Customer
//               </Typography>
//               <FormControl
//                 fullWidth
//                 size="small"
//                 error={!!errors.customer_id}
//                 sx={{
//                   width: { xs: 280, sm: 215, md: 230 },
//                   mb: 0,
//                 }}
//                 disabled={loading}
//               >
//                 <Select
//                   value={selectedCustomerId ?? ""}
//                   onChange={handleCustomerSelect}
//                   displayEmpty
//                   renderValue={(value) => {
//                     if (!value) {
//                       return (
//                         <Box sx={{ color: "text.secondary", }}>
//                           Select customer
//                         </Box>
//                       );
//                     }
//                     if (value === "new") {
//                       return "Add New Customer";
//                     }
//                     return customers.find((c) => c.id === parseInt(value))?.name || "";
//                   }}
//                 >
//                   <MenuItem value="" disabled>
//                     Select customer
//                   </MenuItem>
//                   <MenuItem value="new">
//                     <Button sx={{ background: Color.primary.main, color: '#fff', mt: 1, ml: 2 }}>
//                       + Add New Customer
//                     </Button>
//                   </MenuItem>
//                   <Divider sx={{ border: '1px solid gray' }} />
//                   {customers.map((cust) => (
//                     <MenuItem key={cust.id} value={cust.id.toString()}>
//                       {cust.name}
//                     </MenuItem>
//                   ))}
//                 </Select>
//                 {errors.customer_id && <FormHelperText>{errors.customer_id}</FormHelperText>}
//               </FormControl>
//             </Grid>
//           </Grid>
//           {selectedCustomer && (
//             <>
//               <Typography
//                 sx={{ mb: 0, fontWeight: 600, color: Color.label.main, fontSize: "13px" }}
//               >
//                 Billing Address
//               </Typography>
//               <Paper
//                 elevation={3}
//                 sx={{
//                   p: 2,
//                   mt: 1,
//                   mb: 1,
//                   borderRadius: 3,
//                   bgcolor: "#fff",
//                   boxShadow: "none",
//                   border: "1px solid lightgray",
//                 }}
//               >
//                 <Grid container spacing={2}>
//                   <Grid item xs={12} sm={6}>
//                     <Typography
//                       sx={{ fontSize: { xs: "0.85rem", sm: "0.9rem" }, fontWeight: 600, mb: 0.5 }}
//                     >
//                       Name:
//                       <Typography
//                         component="span"
//                         sx={{
//                           fontWeight: 400,
//                           ml: 1,
//                           fontSize: { xs: "0.85rem", sm: "0.9rem" },
//                         }}
//                       >
//                         {selectedCustomer.name || "-"}
//                       </Typography>
//                     </Typography>
//                     <Typography
//                       sx={{ fontSize: { xs: "0.85rem", sm: "0.9rem" }, fontWeight: 600, mb: 0.5 }}
//                     >
//                       Phone:
//                       <Typography
//                         component="span"
//                         sx={{
//                           fontWeight: 400,
//                           ml: 1,
//                           fontSize: { xs: "0.85rem", sm: "0.9rem" },
//                         }}
//                       >
//                         {selectedCustomer.phone || "-"}
//                       </Typography>
//                     </Typography>
//                     <Typography
//                       sx={{ fontSize: { xs: "0.85rem", sm: "0.9rem" }, fontWeight: 600 }}
//                     >
//                       Address:
//                       <Typography
//                         component="span"
//                         sx={{
//                           fontWeight: 400,
//                           ml: 1,
//                           fontSize: { xs: "0.85rem", sm: "0.9rem" },
//                           whiteSpace: "pre-wrap",
//                         }}
//                       >
//                         {selectedCustomer.address || "-"}
//                       </Typography>
//                     </Typography>
//                   </Grid>
//                 </Grid>
//               </Paper>
//             </>
//           )}

//           <FormControlLabel
//             control={
//               <Checkbox
//                 checked={sameAsAddress}
//                 onChange={(e) => {
//                   const isChecked = e.target.checked;
//                   setSameAsAddress(isChecked);
//                   if (isChecked) {
//                     setFormData((prev) => ({
//                       ...prev,
//                       delivery_address: prev.address,
//                     }));
//                     setErrors((prev) => ({ ...prev, delivery_address: "" }));
//                   }
//                 }}
//                 sx={{
//                   color: Color.primary.main,
//                   "&.Mui-checked": {
//                     color: Color.primary.main,
//                     fontSize:'20px'
//                   },
//                 }}
//               />
//             }
//             label="Delivery address Same as billing address"
//             sx={{ fontSize: { xs: "0.85rem", sm: "0.8rem", md: "0.7rem" } }}
//           />
//           {!sameAsAddress && (
//             <Grid container spacing={2}>
//               <Grid item xs={12} mt={.5}>
//                 <Typography
//                   sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: "13px" }}
//                 >
//                   Delivery Address*
//                 </Typography>
//                 <TextField
//                   fullWidth
//                   name="delivery_address"
//                   value={formData.delivery_address}
//                   onChange={handleChange}
//                   placeholder="Enter delivery address"
//                   multiline
//                   rows={3}
//                   error={!!errors.delivery_address}
//                   helperText={errors.delivery_address}
//                   variant="outlined"
//                   InputLabelProps={{ shrink: false }}
//                   sx={{
//                     width: { xs: 280, sm: 350, md: 530 },
//                     "& .MuiInputLabel-root": { display: "none" },
//                     mb: 0,
//                   }}
//                 />
//               </Grid>
//             </Grid>
//           )}
//         </Paper>

//         <Paper
//           sx={{
//             p: { xs: 2, sm: 3 },
//             borderRadius: 3,
//             bgcolor: "white",
//             boxShadow: "none",
//           }}
//         >
//           <Typography
//             variant="h6"
//             sx={{
//               fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
//               mb: 2,
//               fontWeight: 500,
//             }}
//           >
//             Order Type
//           </Typography>
//           <Grid container spacing={{ xs: 2, sm: 3 }} >
//             <RadioGroup
//               row
//               name="order_type"
//               value={formData.order_type}
//               onChange={handleChange}
//             >
//               {ORDER_TYPE.map((type) => (
//                 <FormControlLabel
//                   key={type.value}
//                   value={type.value}
//                   control={
//                     <Radio
//                       sx={{
//                         "&.Mui-checked": {
//                           color: Color.primary.main,
//                         },
//                       }}
//                     />
//                   }
//                   label={type.label}
//                   sx={{
//                     "& .MuiFormControlLabel-label": {
//                       fontSize: { xs: "0.85rem", sm: "0.9rem" },
//                     },
//                   }}
//                 />
//               ))}
//             </RadioGroup>
//           </Grid>
//         </Paper>

//         <Paper
//           sx={{
//             p: { xs: 2, sm: 3 },
//             borderRadius: 3,
//             bgcolor: "white",
//             boxShadow: "none",
//           }}
//         >
//           <Typography
//             variant="h6"
//             sx={{
//               fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
//               mb: 3,
//               fontWeight: 500,
//             }}
//           >
//             Order Information
//           </Typography>
//           <Grid container spacing={{ xs: 2, sm: 2 }} mb={0}>
//            <Grid item xs={12} sm={6}>
//   <Typography
//     sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: "13px" }}
//   >
//     Order Date*
//   </Typography>
//   <LocalizationProvider dateAdapter={AdapterDateFns}>
//                   <DatePicker
//                     value={formData.order_date}
//                     open={openOrder}
//                     onOpen={() => setOpenOrder(true)}
//                     onClose={() => setOpenOrder(false)}
//                     onChange={(newValue) => {
//                       setFormData((prev) => ({
//                         ...prev,
//                         order_date: newValue,
//                         delivery_date:
//                           prev.delivery_date && newValue && prev.delivery_date < newValue
//                             ? null
//                             : prev.delivery_date,
//                         payment_date:
//                           prev.payment_date && newValue && prev.payment_date < newValue
//                             ? null
//                             : prev.payment_date,
//                       }));
//                       if (!newValue) {
//                         setErrors((prev) => ({
//                           ...prev,
//                           order_date: "Order date is required",
//                         }));
//                       } else {
//                         const today = new Date();
//                         today.setHours(0, 0, 0, 0);
//                         const maxDate = new Date();
//                         maxDate.setFullYear(today.getFullYear() + 1);
//                         if (newValue < today) {
//                           setErrors((prev) => ({
//                             ...prev,
//                             order_date: "Order date cannot be in the past",
//                           }));
//                         } else if (newValue > maxDate) {
//                           setErrors((prev) => ({
//                             ...prev,
//                             order_date: "Order date cannot be more than one year in the future",
//                           }));
//                         } else {
//                           setErrors((prev) => ({ ...prev, order_date: "" }));
//                           setOpenOrder(false);
//                         }
//                       }
//                     }}
//                     // format="yyyy-MM-dd"
//                     format="dd-MM-yyyy"
//                     minDate={new Date()}
//                     maxDate={new Date(new Date().setFullYear(new Date().getFullYear() + 1))}
//                     slotProps={{
//                       textField: {
//                         fullWidth: true,
//                         error: !!errors.order_date,
//                         helperText: errors.order_date,
//                         onClick: () => setOpenOrder(true),
//                         placeholder: "Select order date",
//                         InputLabelProps: { shrink: false },
//                         size: "small",
//                         sx: {
//                           width: { xs: 280, sm: 215, md: 250 },
//                           "& .MuiInputLabel-root": { display: "none" },
//                           mb: 0,
//                         },
//                       },
//                     }}
//                   />
//                 </LocalizationProvider>
// </Grid>

//             <Grid item xs={12} sm={6}>
//               <Typography
//                 sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: "13px" }}
//               >
//                 Delivery Date*
//               </Typography>
//               <LocalizationProvider dateAdapter={AdapterDateFns}>
//                               <DatePicker
//                                 value={formData.delivery_date}
//                                 open={openDelivery}
//                                 onOpen={() => setOpenDelivery(true)}
//                                 onClose={() => setOpenDelivery(false)}
//                                 onChange={(newValue) => {
//                                   setFormData((prev) => ({ ...prev, delivery_date: newValue }));
//                                   if (!newValue) {
//                                     setErrors((prev) => ({
//                                       ...prev,
//                                       delivery_date: "Delivery date is required",
//                                     }));
//                                   } else {
//                                     const maxDate = new Date();
//                                     maxDate.setFullYear(maxDate.getFullYear() + 1);
//                                     const orderDate = formData.order_date || new Date();
//                                     orderDate.setHours(0, 0, 0, 0);
//                                     const newValueDate = new Date(newValue);
//                                     newValueDate.setHours(0, 0, 0, 0);
//                                     if (newValueDate < orderDate) {
//                                       setErrors((prev) => ({
//                                         ...prev,
//                                         delivery_date: "Delivery date must be on or after order date",
//                                       }));
//                                     } else if (newValueDate > maxDate) {
//                                       setErrors((prev) => ({
//                                         ...prev,
//                                         delivery_date: "Delivery date cannot be more than one year in the future",
//                                       }));
//                                     } else {
//                                       setErrors((prev) => ({ ...prev, delivery_date: "" }));
//                                       setOpenDelivery(false);
//                                     }
//                                   }
//                                 }}
//                                 format="yyyy-MM-dd"
//                                 minDate={formData.order_date || new Date()}
//                                 maxDate={new Date(new Date().setFullYear(new Date().getFullYear() + 1))}
//                                 slotProps={{
//                                   textField: {
//                                     fullWidth: true,
//                                     error: !!errors.delivery_date,
//                                     helperText: errors.delivery_date || "",
//                                     onClick: () => setOpenDelivery(true),
//                                     placeholder: "Select delivery date",
//                                     InputLabelProps: { shrink: false },
//                                     size: "small",
//                                     sx: {
//                                       width: { xs: 280, sm: 215, md: 250 },
//                                       "& .MuiInputLabel-root": { display: "none" },
//                                       mb: 0,
//                                     },
//                                   },
//                                 }}
//                               />
//                             </LocalizationProvider>
//             </Grid>
//             <Grid item xs={12} sm={6}>
//               <Typography
//                 sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: "13px" }}
//               >
//                 Salesman Name*
//               </Typography>
//               <TextField
//                 name="salesman_name"
//                 value={formData.salesman_name}
//                 onChange={handleChange}
//                 placeholder="Enter salesman name"
//                 fullWidth
//                 error={!!errors.salesman_name}
//                 helperText={errors.salesman_name}
//                 variant="outlined"
//                 InputLabelProps={{ shrink: false }}
//                 sx={{
//                   width: { xs: 280, sm: 215, md: 250 },
//                   "& .MuiInputLabel-root": { display: "none" },
//                   mb: 0,
//                 }}
//               />
//             </Grid>
//             <Grid item xs={12} sm={6}>
//               <Typography
//                 sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: "13px" }}
//               >
//                 Delivery Type*
//               </Typography>
//               <FormControl
//                 fullWidth
//                 size="small"
//                 error={!!errors.delivery_type}
//                 sx={{
//                   width: { xs: 280, sm: 215, md: 250 },
//                   mb: 0,
//                 }}
//               >
//                 <Select
//                   name="delivery_type"
//                   value={formData.delivery_type ?? ""}
//                   onChange={handleChange}
//                   displayEmpty
//                   renderValue={(value) => {
//                     if (!value) {
//                       return (
//                         <Box sx={{ color: "text.secondary", }}>
//                           Select delivery type
//                         </Box>
//                       );
//                     }
//                     return DELIVERY_TYPE.find((s) => s.value === value)?.label || "";
//                   }}
//                 >
//                   <MenuItem value="" disabled>
//                     Select delivery type
//                   </MenuItem>
//                   {DELIVERY_TYPE.map((s) => (
//                     <MenuItem key={s.value} value={s.value}>
//                       {s.label}
//                     </MenuItem>
//                   ))}
//                 </Select>
//                 {errors.delivery_type && <FormHelperText>{errors.delivery_type}</FormHelperText>}
//               </FormControl>
//             </Grid>
//             <Grid item xs={12} sm={6}>
//               <Typography
//                 sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: "13px" }}
//               >
//                 Packing Charges*
//               </Typography>
//               <TextField
//                 name="packing_charges"
//                 type="number"
//                 value={formData.packing_charges}
//                 onChange={handleChange}
//                 placeholder="Enter packing charges"
//                 fullWidth
//                 error={!!errors.packing_charges}
//                 helperText={errors.packing_charges}
//                 inputProps={{ min: 0 }}
//                 variant="outlined"
//                 InputLabelProps={{ shrink: false }}
//                 sx={{
//                   width: { xs: 280, sm: 215, md: 250 },
//                   "& .MuiInputLabel-root": { display: "none" },
//                   mb: 2,
//                 }}
//               />
//             </Grid>
//             <Grid item xs={12} sm={6}>
//               <Typography
//                 sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: "13px" }}
//               >
//                 Discount (%)
//               </Typography>
//               <TextField
//                 name="discount_amount"
//                 type="number"
//                 value={formData.discount_amount}
//                 onChange={handleChange}
//                 placeholder="Enter discount percentage"
//                 fullWidth
//                 error={!!errors.discount_amount}
//                 helperText={errors.discount_amount}
//                 inputProps={{ min: 0, max: 100 }}
//                 variant="outlined"
//                 InputLabelProps={{ shrink: false }}
//                 sx={{
//                   width: { xs: 280, sm: 215, md: 250 },
//                   "& .MuiInputLabel-root": { display: "none" },
//                   mb: 1,
//                 }}
//               />
//             </Grid>
//             </Grid>
//             <Grid container spacing={{ xs: 2, sm: 2 }} mb={1}>
//               <Grid item xs={12} sm={12}>
//                 <Typography
//                   sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: "13px" }}
//                 >
//                   Notes (optional)
//                 </Typography>
//                 <TextField
//                   name="notes"
//                   value={formData.notes}
//                   onChange={handleChange}
//                   placeholder="Enter notes"
//                   fullWidth
//                   multiline
//                   rows={2}
//                   variant="outlined"
//                   InputLabelProps={{ shrink: false }}
//                   sx={{
//                     width: { xs: 280, sm: 215, md: 515 },
//                     gridColumn: { sm: "span 2" },
//                     "& .MuiInputLabel-root": { display: "none" },
//                     mb: 0,
//                   }}
//                 />
//               </Grid>
//         </Grid>
//               <Typography
//             variant="h6"
//             sx={{
//               fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
//               mt:4,
//               mb: 2,
//               fontWeight: 500,
//             }}
//           >
//             Payment Information
//           </Typography>
//           <Grid container spacing={{ xs: 2, sm: 2 }} mb={1}>
//               <Grid item xs={12} sm={6}>
//               <Typography
//                 sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: "13px" }}
//               >
//                 Payment Method*
//               </Typography>
//               <FormControl
//                 fullWidth
//                 size="small"
//                 error={!!errors.payment_method}
//                 sx={{
//                   width: { xs: 280, sm: 215, md: 250 },
//                   mb: 0,
//                 }}
//               >
//                 <Select
//                   name="payment_method"
//                   value={formData.payment_method ?? ""}
//                   onChange={handleChange}
//                   displayEmpty
//                   renderValue={(value) => {
//                     if (!value) {
//                       return (
//                         <Box sx={{ color: "text.secondary", }}>
//                           Select payment method
//                         </Box>
//                       );
//                     }
//                     return paymentMethods.find((s) => s.value === value)?.label || "";
//                   }}
//                 >
//                   <MenuItem value="" disabled>
//                     Select payment method
//                   </MenuItem>
//                   {paymentMethods.map((s) => (
//                     <MenuItem key={s.value} value={s.value}>
//                       {s.label}
//                     </MenuItem>
//                   ))}
//                 </Select>
//                 {errors.payment_method && <FormHelperText>{errors.payment_method}</FormHelperText>}
//               </FormControl>
//             </Grid>
//             <Grid item xs={12} sm={6}>
//               <Typography
//                 sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: "13px" }}
//               >
//                 Total
//               </Typography>
//               <TextField
//                 value={totals.total.toFixed(0)}
//                 InputProps={{ readOnly: true }}
//                 variant="outlined"
//                 InputLabelProps={{ shrink: false }}
//                 sx={{
//                   width: { xs: 280, sm: 215, md: 250 },
//                   bgcolor: "#f1f1f1ee",
//                   "& .MuiInputLabel-root": { display: "none" },
//                   mb: 1,
//                 }}
//               />
//             </Grid>
//             <Grid item xs={12} sm={6}>
//               <Typography
//                 sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: "13px" }}
//               >
//                 Advance* ( <span style={{ fontWeight: 400, color: Color.label.main, }}>
//                   {formData.payment_date
//                     ? formData.payment_date.toLocaleDateString("en-GB")
//                     : errors.payment_date
//                       ? errors.payment_date
//                       : "Advance  date "}</span>)
//               </Typography>
//               <TextField
//                 type="number"
//                 name="advance_amount"
//                 value={formData.advance_amount}
//                 onChange={handleChange}
//                 placeholder="Enter advance "
//                 error={!!errors.advance_amount}
//                 helperText={errors.advance_amount}
//                 inputProps={{ min: 0 }}
//                 variant="outlined"
//                 InputLabelProps={{ shrink: false }}
//                 sx={{
//                   width: { xs: "100%", sm: 215, md: 250 },
//                   "& .MuiInputLabel-root": { display: "none" },
//                   mb: 1,
//                 }}
//                 InputProps={{
//                   endAdornment: (
//                     <InputAdornment position="end">
//                       <LocalizationProvider dateAdapter={AdapterDateFns}>
//                         <Stack direction="row" alignItems="center" spacing={2}>
//                           <IconButton
//                             sx={{ p: 1 }}
//                             ref={iconRef}
//                             onClick={() => setOpenAdvance(true)}
//                           >
//                             <CalendarTodayIcon sx={{ fontSize: "20px", color: Color.primary.main }} />
//                           </IconButton>
//                         </Stack>
//                         <DatePicker
//                           open={openAdvance}
//                           onClose={() => setOpenAdvance(false)}
//                           value={formData.payment_date}
//                           onChange={(newValue) => {
//                             setFormData((prev) => ({ ...prev, payment_date: newValue }));
//                             if (!newValue) {
//                               setErrors((prev) => ({
//                                 ...prev,
//                                 payment_date: "Advance date is required",
//                               }));
//                             } else if (formData.order_date && newValue < formData.order_date) {
//                               setErrors((prev) => ({
//                                 ...prev,
//                                 payment_date: "Advance date must be on or after order date",
//                               }));
//                             } else {
//                               setErrors((prev) => ({ ...prev, payment_date: "" }));
//                             }
//                           }}
//                           minDate={new Date()}
//                           slots={{
//                             textField: () => null,
//                           }}
//                           slotProps={{
//                             popper: {
//                               anchorEl: iconRef.current,
//                               placement: "bottom-start",
//                             },
//                           }}
//                           enableAccessibleFieldDOMStructure={false}
//                         />
//                       </LocalizationProvider>
//                     </InputAdornment>
//                   ),
//                 }}
//               />
//             </Grid>
//             <Grid item xs={12} sm={6}>
//               <Typography
//                 sx={{ mb: 1, fontWeight: 600, color: Color.label.main, fontSize: "13px" }}
//               >
//                 Pending
//               </Typography>
//               <TextField
//                 value={formData.pending}
//                 InputProps={{ readOnly: true }}
//                 variant="outlined"
//                 InputLabelProps={{ shrink: false }}
//                 sx={{
//                   width: { xs: "100%", sm: 215, md: 250 },
//                   bgcolor: "#f1f1f1ee",
//                   "& .MuiInputLabel-root": { display: "none" },
//                   mb: 0,
//                 }}
//               />
//             </Grid>
//         </Grid>
//         </Paper>
//       </Box>

//       <Box
//         sx={{
//           flex: { xs: "1 1 100%", md: "1 1 30%" },
//           position: { md: "sticky", xs: "static" },
//           top: { md: 17 },
//           alignSelf: { md: "flex-start" },
//           height: { xs: "auto", sm: "70vh", md: "81vh" },
//         }}
//       >
//         <Paper
//           sx={{
//             p: { xs: 2, sm: 3 },
//             borderRadius: 3,
//             display: "flex",
//             flexDirection: "column",
//             height: "100%",
//           }}
//         >
//           <Typography
//             variant="h6"
//             sx={{
//               fontWeight: 500,
//               mb: 2,
//               fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
//             }}
//           >
//             Order Details
//           </Typography>
//           <Box
//             sx={{
//               flex: 1,
//               overflowY: "auto",
//               pr: { xs: 0, sm: 1 },
//               maxHeight: { xs: "auto", sm: "calc(70vh - 200px)", md: "calc(100vh - 200px)" },
//             }}
//           >
//             {cartItems.map((item) => {
//               const price = parseFloat(item.base_price || 0);
//               const total = price * item.quantity;
//               return (
//                 <Box
//                   key={item.id}
//                   sx={{
//                     display: "flex",
//                     justifyContent: "space-between",
//                     py: 2,
//                     borderBottom: "1px solid #f0f0f0",
//                     flexWrap: "wrap",
//                   }}
//                 >
//                   <Box sx={{ flex: 1, minWidth: 120 }}>
//                     <Typography
//                       variant="body2"
//                       sx={{
//                         fontWeight: 500,
//                         fontSize: { xs: "0.8rem", sm: "0.85rem" },
//                       }}
//                     >
//                       {item.name}
//                     </Typography>
//                     <Typography
//                       variant="caption"
//                       color="text.secondary"
//                       sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
//                     >
//                       x {item.quantity}
//                     </Typography>
//                   </Box>
//                   <Box
//                     sx={{
//                       display: "flex",
//                       flexDirection: "column",
//                       alignItems: "flex-end",
//                       gap: 1,
//                     }}
//                   >
//                     <Typography
//                       variant="body2"
//                       sx={{
//                         fontWeight: 500,
//                         fontSize: { xs: "0.8rem", sm: "0.85rem" },
//                       }}
//                     >
//                       ₹ {total.toFixed(2)}
//                     </Typography>
//                     <Box sx={{ display: "flex", gap: 0.5 }}>
//                       <IconButton
//                         onClick={() => updateQuantity(item.id, -1)}
//                         size="small"
//                       >
//                         <Remove fontSize="small" />
//                       </IconButton>
//                       <IconButton
//                         onClick={() => updateQuantity(item.id, 1)}
//                         size="small"
//                       >
//                         <Add fontSize="small" />
//                       </IconButton>
//                     </Box>
//                   </Box>
//                 </Box>
//               );
//             })}
//             {cartItems.length === 0 && (
//               <Typography
//                 variant="body2"
//                 color="error"
//                 textAlign="center"
//                 py={4}
//                 sx={{ fontSize: { xs: "0.8rem", sm: "0.9rem" } }}
//               >
//                 No items in cart.
//               </Typography>
//             )}
//           </Box>
//           <Box mt={2} pt={2} borderTop="1px solid #eee">
//             <Typography
//               color="text.secondary"
//               sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
//             >
//               Subtotal: ₹{totals.subtotal.toFixed(2)}
//             </Typography>
//             <Typography
//               color="text.secondary"
//               sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
//             >
//               Packing Charges: ₹{(parseFloat(formData.packing_charges) || 0).toFixed(2)}
//             </Typography>
//             <Typography
//               color="text.secondary"
//               sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
//             >
//               Discount ({formData.discount_amount || 0}%): ₹{((totals.subtotal * (parseFloat(formData.discount_amount) || 0)) / 100).toFixed(2)}
//             </Typography>
//             <Typography
//               fontWeight={600}
//               mt={1}
//               sx={{ fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" } }}
//             >
//               Total: ₹{totals.total.toFixed(0)}
//             </Typography>
//             <Box mt={3}>
//               <Button
//                 fullWidth
//                 color="primary"
//                 onClick={handleSubmit}
//                 disabled={!isFormValid() || submitting}
//                 sx={{
//                   py: 1.3,
//                   borderRadius: 2,
//                   fontWeight: 600,
//                   background: Color.primary.main,
//                   color: "white",
//                   "&:disabled": {
//                     background: "#7e7a7aff",
//                     color: "#f3f0f0ff",
//                     cursor: "not-allowed",
//                     pointerEvents: "auto",
//                   },
//                   fontSize: { xs: "0.85rem", sm: "0.9rem" },
//                 }}
//               >
//                 {submitting ? "Creating..." : "Create Order"}
//               </Button>
//               <Button
//                 fullWidth
//                 variant="outlined"
//                 onClick={() => navigate("/create-order")}
//                 sx={{
//                   mt: 2,
//                   py: 1.3,
//                   borderRadius: 2,
//                   borderColor: Color.secondary.main,
//                   color: Color.secondary.main,
//                   fontSize: { xs: "0.85rem", sm: "0.9rem" },
//                   "&:hover": {
//                     borderColor: Color.secondary.dark,
//                     color: Color.secondary.dark,
//                     backgroundColor: "transparent",
//                   },
//                 }}
//               >
//                 Back to Products
//               </Button>
//             </Box>
//           </Box>
//         </Paper>
//       </Box>
//       <AddCustomer
//         open={openModal}
//         onClose={resetForm}
//         formData={formData}
//         setFormData={setFormData}
//         onSubmit={handleCustomer}
//       />
//     </Box>
//   );
// };

// export default OrderDetails;